import {
  makeApiProvider,
  useApiQuery,
  usePhoenixApiInfiniteQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type {
  GetPliantTransactionsParams,
  GetPliantTransactionsResponse,
  IPliantPhoenixApi,
  PliantTransaction,
} from './api';
import { PliantPhoenixApi, PliantStatus, PliantType } from './api';
import { PLIANT_TRANSACTION_KEY, PLIANT_TRANSACTIONS_KEY } from './queryKeys';

export type {
  PliantTransaction,
  GetPliantTransactionsParams,
  GetPliantTransactionsResponse,
};

const ApiContext = createContext<IPliantPhoenixApi | undefined>(undefined);

const { useApi: usePliantPhoenixApi, ApiProvider: PliantPhoenixApiProvider } =
  makeApiProvider({
    name: 'PliantPhoenixApi',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new PliantPhoenixApi(tenantId, auth, apiUrl),
  });

export {
  usePliantPhoenixApi,
  PliantPhoenixApiProvider,
  ApiContext as PliantPhoenixApiContext,
  PliantStatus,
  PliantType,
};

export function usePliantTransaction(
  params: { pliant_transaction_id: string; legal_entity_id: string },
  options?: Omit<
    UseQueryOptions<{ data: PliantTransaction }, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<{ data: PliantTransaction }, unknown> {
  const select = useCallback((data: { data: PliantTransaction }) => {
    return {
      data: {
        ...data.data,
        booked_at: new Date(data.data.booked_at),
        created_at: new Date(data.data.created_at),
        authorized_at: new Date(data.data.authorized_at),
        confirmed_at: new Date(data.data.confirmed_at),
        reversed_at: data.data.reversed_at
          ? new Date(data.data.reversed_at)
          : null,
      },
    };
  }, []);

  return useApiQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTION_KEY(params.pliant_transaction_id),
    (api) =>
      api.getPliantTransaction({
        params: params,
      }),
    {
      ...options,
      select,
    }
  );
}

export function usePliantTransactions(
  params: GetPliantTransactionsParams,
  options?: Omit<
    UseQueryOptions<GetPliantTransactionsResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetPliantTransactionsResponse, unknown> {
  return useApiQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTIONS_KEY(params.params),
    (api) => api.getPliantTransactions(params),
    {
      enabled: params.params.legal_entity_id !== '',
      ...options,
    }
  );
}

export function getIncompletePliantTransactionsParams(
  page: number,
  pageSize: number,
  selectedNursingHome:
    | {
        legalEntityId: string;
        id: string;
      }
    | undefined,
  employeeIds: string[] | undefined
): GetPliantTransactionsParams {
  return {
    params: {
      page: page,
      page_size: pageSize,
      legal_entity_id: selectedNursingHome?.legalEntityId ?? '',
      nursing_home_id: selectedNursingHome?.id ?? '',
      employee_ids: employeeIds,
      mismatch: true,
      pliant_type: [PliantType.PURCHASE, PliantType.CASH_WITHDRAWAL],
      pliant_status: [
        PliantStatus.PENDING,
        PliantStatus.CONFIRMED,
        PliantStatus.BOOKED,
      ],
    },
  };
}

export function usePliantTransactionsInfiniteQuery({
  legal_entity_id,
  ...otherParams
}: Omit<GetPliantTransactionsParams['params'], 'page' | 'page_size'> & {
  page_size?: number;
}): UseInfiniteQueryResult<
  InfiniteData<GetPliantTransactionsResponse>,
  unknown
> {
  const pageSize = otherParams.page_size ?? 20;

  return usePhoenixApiInfiniteQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTIONS_KEY({
      legal_entity_id,
      page: 1,
      page_size: pageSize,
      ...otherParams,
    }),
    async (api, pageParam) => {
      if (legal_entity_id === '') {
        throw new Error('legal_entity_id is empty');
      }

      return await api.getPliantTransactions({
        params: {
          legal_entity_id,
          page: pageParam as number,
          page_size: pageSize,
          ...otherParams,
        },
      });
    },
    { enabled: legal_entity_id !== '' }
  );
}
